'use client';

import { Search, X } from 'lucide-react';
import Form from 'next/form';
import { KeyboardEvent, useRef, useState } from 'react';

import { pseudoI18n } from '@/i18n/pseudoI18n';
import { cn } from '@/utilities/cn';
import Popover from '@/globals/Header/components/Popover/index';

export default function MHeaderSearch() {
  const [ expanded, setExpanded ] = useState( false );
  const [ searchQuery, setSearchQuery ] = useState( '' );
  const [ showNotices, setShowNotices ] = useState<boolean>( false );

  const formRef = useRef<HTMLFormElement>( null );
  const onResetForm = () => setSearchQuery( '' );

  const { t } = pseudoI18n();

  const onToggleExpand = () => {
    const newState = !expanded;
    setExpanded( newState );
    if ( !newState ) onResetForm();
  };

  const onKeyDownHandler = ( e: KeyboardEvent<HTMLInputElement> ) => {
    if ( e.key === 'Enter' ) {
      setExpanded( false );
    }
  };

  const handleSubmit = ( e: React.FormEvent<HTMLFormElement> ) => {
    if ( searchQuery.trim().length < 2 ) {
      e?.preventDefault();
      setShowNotices( true );
      setExpanded( true );
      return;
    }
  };

  return (
    <div className="w-8">
      <button
        onClick={ onToggleExpand }
        className="ml-auto block relative"
        aria-label={ t( 'Search' ) }
      >
        <div className={ cn(
          'text-white bg-secondary rounded-full w-7 h-7 flex items-center justify-center opacity-100 translate-y-0 transition-all duration-500',
          expanded && 'opacity-0 pointer-events-none -translate-y-full',
        ) }>
          <Search width={ 20 }/>
        </div>
        <div className={ cn(
          "transition-all duration-500 text-secondary absolute top-full right-0 bottom-0 opacity-0 pointer-events-none flex justify-center items-center h-7",
          expanded && 'opacity-100 pointer-events-auto top-0',
          !expanded && 'h-0'
        ) }>
          { t( 'Close' ) }
        </div>
      </button>
      <Form
        action="/search"
        className={ cn(
          'z-30 absolute top-14 left-0 right-0 bg-white flex px-4 gap-2 w-full opacity-100 translate-y-0 transition-all duration-500 max-h-14 overflow-hidden',
          !expanded && 'max-h-0 opacity-0 pointer-events-none'
        ) }
        ref={ formRef }
        onSubmit={ handleSubmit }
      >
        <label
          htmlFor="search-mobile"
          className="flex flex-1 items-center relative border border-secondary rounded-lg px-2 py-1 my-1.5"
        >
          <span className="opacity-0 invisible w-0 overflow-hidden block">{ t( 'Search' ) }</span>
          <Search width={ 20 }/>
          <input
            name="q"
            id="search-mobile"
            placeholder={ t( 'Search' ) }
            className="w-full mx-2 px-1"
            onKeyDown={ onKeyDownHandler }
            onChange={ ( e ) => setSearchQuery( e.target.value ) }
            value={ searchQuery }
          />
          <button
            type="button"
            onClick={ onResetForm }
            className='rounded-full bg-neutral-300 p-0.5'
            aria-label={ t( 'Clear search' ) }
          >
            <X width={ 24 } height={ 24 } color='white'/>
          </button>
        </label>
      </Form>
      <Popover
        showNotices={ showNotices }
        setShowNotices={ setShowNotices }
        content={ t( 'Please enter at least 2 characters.' ) }
      />
    </div>
  );
}
